import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { Clock, Info, MapPin, Phone, User } from 'lucide-react'

import { type CampusInfoRow, visibleCampusInfoRows } from '@/lib/chefs/campus-info'
import type { Campus } from '@/lib/chefs/types'

interface CampusInfoDialogProps {
  campus: Campus
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ROW_ICONS: Record<CampusInfoRow['key'], typeof Clock> = {
  pocName: User,
  phone: Phone,
  deliveryWindow: Clock,
  lunchStart: Clock,
  parking: MapPin,
  notes: Info,
}

function Row({ Icon, label, children }: { Icon: typeof Clock; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="flex w-32 shrink-0 items-center gap-1.5 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        {label}
      </span>
      <span className="flex-1 text-foreground">{children}</span>
    </div>
  )
}

export function CampusInfoDialog({ campus, open, onOpenChange }: CampusInfoDialogProps) {
  const { info } = campus
  const fullAddress = `${info.addressLine1}, ${info.city} ${info.state} ${info.zip}`
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
  const rows = visibleCampusInfoRows(info)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Campus information · {campus.name}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <Row Icon={MapPin} label="Address">
            <a href={mapsHref} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              {fullAddress}
            </a>
          </Row>
          {rows.map((row) => (
            <Row key={row.key} Icon={ROW_ICONS[row.key]} label={row.label}>
              {row.key === 'phone' ? (
                <a href={`tel:${row.value.replace(/[^0-9+]/g, '')}`} className="text-primary hover:underline">
                  {row.value}
                </a>
              ) : (
                row.value
              )}
            </Row>
          ))}
          {rows.length === 0 ? <p className="text-sm text-muted-foreground">No delivery details on file yet.</p> : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
